import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FieldFilter, PackSearchContent, SearchPayload, SearchResult } from '@shared/models/search.model';
import { SearchService } from '@services/search.service';
import { Subject } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { DataRef, Domain, Market } from '@shared/models/dataref.model';
import {
  DisplayData,
  DomainMarketLangList,
  FilterConfig,
  LoadDataEvent,
} from '@shared/components/data-table-manager/data-table-manager.component';
import { ActivatedRoute } from '@angular/router';
import { ConfigService } from '@services/config.service';
import { DomainMarketLang } from '@shared/models/comments/comment.model';
import { ColumnConfiguration } from '@shared/models/generic/column-configuration.model';
import { DataRefUtils } from '@shared/utils/data-ref-utils';
import { TranslateService } from '@ngx-translate/core';
import { ResourceMediaLite } from '@shared/models/generic/media/resource-media-lite.model';
import { takeUntil } from 'rxjs/operators';
import { FacetGroup } from '@shared/components/facet-list/facet-list.component';
import { FieldsFilterDomainMarketLang } from '@shared/components/filter-domain-market-lang/filter-domain-market-lang.component';
import { ADMIN_ROLE_ID } from '@shared/constants/constant';
import { RefDataService } from '@services/ref-data.service';

@Component({
  selector: 'app-packs-search',
  templateUrl: './packs-search.component.html',
  styleUrls: ['./packs-search.component.scss'],
  standalone: false,
})
export class PacksSearchComponent implements OnInit, OnDestroy {
  columnConfigs: ColumnConfiguration<PackSearchContent>[] = [];
  tableData = new MatTableDataSource<PackSearchContent>([]);
  domains: Domain[] = [];
  markets: Market[] = [];

  domainMarketLang: DomainMarketLangList = {
    domains: [],
    markets: [],
    languages: [],
  };

  packDomainMarketLang: DomainMarketLang = {
    domain: '',
    market: '',
    lang: '',
  };
  fieldFilters: FieldFilter[];
  pageIndex = 0;
  pageSize = 20;
  displayInfo: DisplayData = {
    resultsMessage: '',
    dataLength: 0,
    noDataMessage: 'REFDATA.PACKS.SEARCH.NO_RESULT_FOR_QUERY',
    totalPages: 0,
  };
  fieldsFilterDomainMarketLang: FieldsFilterDomainMarketLang = {
    domainFieldFilter: 'domain.key',
    marketFieldFilter: 'market.key',
    langFieldFilter: 'name.lang',
  };

  facetGroups: Subject<FacetGroup[]> = new Subject<FacetGroup[]>();

  DEFAULT_FILTER_CONFIG: FilterConfig = {
    isFacetFilterEnabled: true,
    isFieldFilterEnabled: true,
    isSearchEnabled: true,
  };

  defaultFilter: FieldFilter = { field: 'status.key', values: ['DELETED'], type: 'exclusion' };
  defaultSortActive = 'modificationDate';
  defaultSortDirection: 'asc' | 'desc' = 'desc';

  searchPayload: SearchPayload = {
    facetList: [{ name: 'status' }],
    fieldList: ['key', 'type', 'medias', 'name', 'domain', 'status', 'creator', 'modifier', 'creationDate', 'modificationDate'],
  };

  private packStatuses: DataRef[] = [];
  private readonly destroy$: Subject<void> = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly searchService: SearchService,
    private readonly refDataService: RefDataService,
    private readonly cdr: ChangeDetectorRef,
    private readonly configService: ConfigService,
    private readonly translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.initDomainMarketLang();
    this.createColumnConfig();
    const resolvedData = this.route.snapshot.data.packData;
    this.domains = resolvedData?.domains;
    this.markets = resolvedData?.markets;
    this.domainMarketLang.domains = resolvedData?.domains;
    this.domainMarketLang.markets = resolvedData?.markets;
    this.domainMarketLang.languages = resolvedData?.languages;
    this.refDataService
      .getByKey('packStatuses', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((statuses: any) => {
        this.packStatuses = this.refDataService.translateReference(this.refDataService.toArray(statuses));
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initDomainMarketLang(): void {
    const defaultSearchQueryParams = this.configService.getDefaultSearchQueryParams();
    this.packDomainMarketLang.domain = defaultSearchQueryParams.domain;
    this.packDomainMarketLang.market = defaultSearchQueryParams.market;
    this.packDomainMarketLang.lang = defaultSearchQueryParams.lang;
  }

  handleGlobalFilterChange(fieldFilter: FieldFilter[]): void {
    this.fieldFilters = fieldFilter.slice();
  }

  loadData(event: LoadDataEvent): void {
    this.executeLoadData(event);
  }

  private executeLoadData(event: LoadDataEvent): void {
    this.searchPayload.facetFilters = event.facetFilters;
    if (this.isConnectedUserAdmin()) {
      this.searchPayload.fieldFilters = event.fieldFilters;
    } else {
      this.searchPayload.fieldFilters = [this.defaultFilter, ...event.fieldFilters];
    }

    if (event?.sort?.active && event?.sort?.direction) {
      this.searchPayload.sort = {
        name: event.sort.active,
        direction: event.sort.direction,
      };
    }

    // If the fieldFilter lang is not set, we use the current language as default
    const lang =
      this.getSingleFilterValue(this.searchPayload.fieldFilters, this.fieldsFilterDomainMarketLang.langFieldFilter) ??
      this.configService.getCurrentLanguage();

    // If the fieldFilter market is not set, we use the market associated to the current language as default
    const market =
      this.getSingleFilterValue(this.searchPayload.fieldFilters, this.fieldsFilterDomainMarketLang.marketFieldFilter) ??
      (lang === 'fr' ? 'GS_FR' : 'GS_INT');

    this.searchService
      .searchPackReferences(
        this.searchPayload,
        event.filterParams.query,
        event.filterParams.paginator?.pageSize ?? this.pageSize,
        event.filterParams.paginator?.pageIndex ?? this.pageIndex,
        market,
        lang,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: SearchResult<PackSearchContent>) => {
        this.tableData.data = data.content.map((pack: PackSearchContent, index: number) =>
          this.mapPackSearchContentToTableData(pack, index),
        );
        this.updateDisplayInfo(data.page.totalElements, data.page.totalPages);
        this.facetGroups.next([{ title: 'FACETS.STANDARD', facets: data.facets }]);
      });
  }

  private createColumnConfig(): void {
    this.columnConfigs = [
      {
        dataFieldName: 'actions',
        name: 'actions',
        headerTranslationKey: 'SEARCH_ACTION',
        content: {
          type: 'action',
          actions: [
            {
              label: 'Edit',
              type: 'edit',
              icon: 'edit',
              callback: (element: PackSearchContent) => {
                window.open(`references/packs/edit/${element.key}`, '_blank');
              },
            },
          ],
        },
        sortable: false,
      },
      {
        dataFieldName: 'domain',
        name: 'domain',
        headerTranslationKey: 'SEARCH_DOMAIN',
        content: { type: 'string', value: (element: PackSearchContent) => element.domain },
        sortable: true,
      },
      {
        dataFieldName: 'image',
        name: 'image',
        headerTranslationKey: 'SEARCH_IMAGE',
        content: { type: 'string', value: () => '', isImage: true },
        sortable: false,
      },
      {
        dataFieldName: 'title',
        name: 'name',
        headerTranslationKey: 'SEARCH_NAME',
        content: { type: 'string', value: (element: PackSearchContent) => element.title },
        sortable: true,
        linkCallback: (element: PackSearchContent) => {
          window.open(`references/packs/edit/${element.key}`, '_blank');
        },
      },
      {
        dataFieldName: 'market',
        name: 'market',
        headerTranslationKey: 'SEARCH_MARKET',
        content: { type: 'string', value: (element: PackSearchContent) => element.market },
        sortable: true,
      },
      {
        dataFieldName: 'language',
        name: 'language',
        headerTranslationKey: 'SEARCH_LANG',
        content: { type: 'string', value: (element: PackSearchContent) => element.language },
        sortable: false,
      },
      {
        dataFieldName: 'status',
        name: 'status',
        headerTranslationKey: 'SEARCH_STATUS',
        content: { type: 'string', value: (element: PackSearchContent) => element.status, isStatus: true },
        sortable: true,
      },
      {
        dataFieldName: 'modifier',
        name: 'modifier.nickname',
        headerTranslationKey: 'SEARCH_MODIFIED_BY',
        content: { type: 'string', value: (element: PackSearchContent) => element.modifier },
        sortable: true,
      },
      {
        dataFieldName: 'modificationDate',
        name: 'modificationDate',
        headerTranslationKey: 'SEARCH_MODIFICATION_DATE',
        content: { type: 'date', format: 'dd/MM/yyyy' },
        sortable: true,
      },
      {
        dataFieldName: 'creator',
        name: 'creator.nickname',
        headerTranslationKey: 'SEARCH_CREATED_BY',
        content: { type: 'string', value: (element: PackSearchContent) => element.creator },
        sortable: true,
      },
      {
        dataFieldName: 'creationDate',
        name: 'creationDate',
        headerTranslationKey: 'SEARCH_CREATION_DATE',
        content: { type: 'date', format: 'dd/MM/yyyy' },
        sortable: true,
      },
    ];
  }

  private updateDisplayInfo(totalElements: number, totalPages: number): void {
    const resultsMessage = `${totalElements} ${
      totalElements === 1
        ? this.translateService.instant('REFDATA.PACKS.SEARCH.RESULT_FOUND')
        : this.translateService.instant('REFDATA.PACKS.SEARCH.RESULTS_FOUND')
    }`;

    this.displayInfo = {
      ...this.displayInfo,
      dataLength: totalElements,
      resultsMessage,
      totalPages,
    };
  }

  private mapPackSearchContentToTableData(pack: PackSearchContent, index: number): PackSearchContent {
    const foundStatus = this.packStatuses.find((s) => s.key === pack.status);
    const translatedStatus = foundStatus?.translatedName ?? pack.status;
    return {
      domain: DataRefUtils.getLocalizedValueByKey(
        this.domainMarketLang.domains,
        pack.domain,
        this.packDomainMarketLang.market,
        this.packDomainMarketLang.lang,
      ),
      market: DataRefUtils.getLocalizedValueByKey(
        this.domainMarketLang.markets,
        pack.name?.[0].market,
        this.packDomainMarketLang.market,
        this.packDomainMarketLang.lang,
      ),
      language: DataRefUtils.getLocalizedValueByKey(
        this.domainMarketLang.languages,
        pack.name?.[0].lang,
        this.packDomainMarketLang.market,
        this.packDomainMarketLang.lang,
      ),
      title: pack.name?.[0].value,
      image: pack.medias?.find((media: ResourceMediaLite) => media.isCover)?.media?.thumbnail,
      status: translatedStatus,
      name: pack.name,
      modifier: pack.modifier,
      creationDate: pack.creationDate,
      modificationDate: pack.modificationDate,
      creator: pack.creator,
      key: pack.key,
      position: index,
    };
  }

  /**
   * Retrieve profile infos from localStorage and
   * Check if the connected user is an admin
   *
   * @private
   */
  private isConnectedUserAdmin(): boolean {
    const profile = JSON.parse(localStorage.getItem('profile') ?? '{}');
    return profile?.authorizations?.role === ADMIN_ROLE_ID;
  }

  private getSingleFilterValue(filters: FieldFilter[], field: string): string | undefined {
    const value = filters?.find((f) => f.field === field)?.values?.[0];
    return typeof value === 'string' ? value : undefined;
  }
}
